import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { walletApi, Wallet as WalletType, Transaction, WithdrawalRequest, SavedBankDetails } from '../api';
import { Wallet as WalletIcon, Plus, ArrowUpRight, ArrowDownLeft, TrendingUp, Clock, X, Loader2, Check, AlertCircle, ChevronDown, Landmark } from 'lucide-react';
import Layout from '../components/Layout';
import PaymentSheet from '../components/PaymentSheet';
import EmptyState from '../components/EmptyState';
import { Card, Button } from '../components/ui';
import { apiErrorMessage } from '../utils/errors';

const SimpleWallet: React.FC = () => {
  const [wallet, setWallet] = useState<WalletType | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [depositAmount, setDepositAmount] = useState(200);
  const [customDeposit, setCustomDeposit] = useState('');
  const [depositError, setDepositError] = useState('');
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  const [showWithdrawalForm, setShowWithdrawalForm] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [bank, setBank] = useState<SavedBankDetails | null>(null);
  const [isProcessingWithdrawal, setIsProcessingWithdrawal] = useState(false);
  const navigate = useNavigate();
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [expandedTransaction, setExpandedTransaction] = useState<number | null>(null);

  const depositStatus =
    typeof window !== 'undefined' && window.location.pathname.includes('/deposit/return')
      ? 'return'
      : typeof window !== 'undefined' && window.location.pathname.includes('/deposit/cancel')
      ? 'cancel'
      : null;
  const [banner, setBanner] = useState<'return' | 'cancel' | null>(depositStatus);

  const walletMessages = [
    "Your wallet is looking healthier than your golf swing.",
    "Enough for a few brave bets and at least one terrible idea.",
    "This balance has more potential than your driver.",
    "Ready to turn this into prize money?",
    "More than enough for golf balls. Dignity costs extra."
  ];

  const randomWalletMessage = walletMessages[Math.floor(Math.random() * walletMessages.length)];
  const quickAmounts = [100, 200, 500, 1000];

  useEffect(() => {
    loadData();
  }, []);

  // Payouts can land while this screen is open in another tab. Keep the shown
  // balance and activity current, and refresh immediately when the user returns.
  useEffect(() => {
    const sync = () => { if (document.visibilityState === 'visible') void loadData(); };
    const timer = window.setInterval(sync, 5000);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  // Load the saved payout account when the withdraw form opens.
  useEffect(() => {
    if (!showWithdrawalForm) return;
    let active = true;
    walletApi.getSavedBankDetails()
      .then((d) => { if (active) setBank(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [showWithdrawalForm]);

  useEffect(() => {
    // The PayFast ITN can land a moment after the user returns, so refetch
    // the wallet shortly after a successful return.
    if (depositStatus === 'return') {
      const timer = setTimeout(() => loadData(), 2500);
      return () => clearTimeout(timer);
    }
  }, [depositStatus]);

  const loadData = async () => {
    try {
      const [walletData, transactionsData, withdrawalsData] = await Promise.all([
        walletApi.getWallet(),
        walletApi.getTransactions(),
        walletApi.getWithdrawalRequests(),
      ]);
      setWallet(walletData && typeof walletData === 'object' ? walletData : null);
      setRecentTransactions(Array.isArray(transactionsData) ? transactionsData.slice(0, 10) : []);
      setWithdrawals(Array.isArray(withdrawalsData) ? withdrawalsData : []);
    } catch (error) {
      console.error('Failed to load wallet:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWithdrawal = async () => {
    if (!wallet || !withdrawalAmount || !bank?.has_details) return;

    const amountCents = Math.round(parseFloat(withdrawalAmount) * 100);
    // Client-side checks so users get instant, readable feedback.
    if (isNaN(amountCents) || amountCents < 10000) {
      alert('Minimum withdrawal is R100.');
      return;
    }
    if (amountCents > 1000000) {
      alert('Maximum withdrawal is R10,000.');
      return;
    }
    if (!bank?.has_details) {
      alert('Add your payout bank account in your profile before withdrawing.');
      return;
    }

    setIsProcessingWithdrawal(true);
    try {
      // Bank details come from the saved profile account.
      await walletApi.createWithdrawalRequest(amountCents);

      // Reset form
      setWithdrawalAmount('');
      setShowWithdrawalForm(false);

      // Show success message and refresh balance + withdrawal list.
      alert('Withdrawal request submitted! 3-day cooling off period applies.');
      loadData();

    } catch (error: any) {
      alert(apiErrorMessage(error, 'Failed to create withdrawal request'));
    } finally {
      setIsProcessingWithdrawal(false);
    }
  };

  const handleCancelWithdrawal = async (id: number) => {
    setCancellingId(id);
    try {
      await walletApi.cancelWithdrawal(id);
      loadData();
    } catch (error: any) {
      alert(apiErrorMessage(error, 'Failed to cancel withdrawal'));
    } finally {
      setCancellingId(null);
    }
  };

  const withdrawalStatusChip = (status: string) => {
    switch (status) {
      case 'cooling_off':
        return { label: 'Cooling off', className: 'bg-amber-100 text-amber-700' };
      case 'ready':
        return { label: 'Ready for payout', className: 'bg-blue-100 text-blue-700' };
      case 'processing':
        return { label: 'Processing', className: 'bg-blue-100 text-blue-700' };
      case 'completed':
        return { label: 'Paid out', className: 'bg-emerald-100 text-emerald-700' };
      case 'cancelled':
        return { label: 'Cancelled', className: 'bg-gray-100 text-gray-500' };
      default:
        return { label: status, className: 'bg-gray-100 text-gray-600' };
    }
  };

  // Deposits, winnings, and refunds add money; stakes and withdrawals remove it.
  const isCredit = (type: string) => ['deposit', 'bet_won', 'bet_refund'].includes(type);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownLeft className="h-4 w-4 text-green-600" />;
      case 'bet_won':
      case 'bet_refund':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'withdrawal':
        return <ArrowUpRight className="h-4 w-4 text-red-600" />;
      default:
        return <TrendingUp className="h-4 w-4 text-blue-600" />;
    }
  };

  const getTransactionColor = (type: string) => {
    if (isCredit(type)) return 'text-green-600';
    switch (type) {
      case 'withdrawal':
      case 'bet_placed':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto py-20 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-2xl mx-auto space-y-6 pb-20">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Wallet</h1>
          <p className="text-sm text-green-200">Deposits, bets, and payouts in one place.</p>
        </div>

        {banner && (
          <div
            className={`rounded-xl px-4 py-3 text-sm flex items-start justify-between ${
              banner === 'return' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
            }`}
          >
            <span>
              {banner === 'return'
                ? 'Thanks! Your payment is being processed — your balance will update shortly.'
                : 'Payment cancelled. No funds were taken.'}
            </span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss" className="ml-3 p-1 rounded-full hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400/70">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-brand-800 to-brand-950 text-white border border-accent-400/30 shadow-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 p-3 rounded-xl">
                <WalletIcon className="h-6 w-6" />
              </div>
              <div>
                <p className="text-white/80 text-sm">Available Balance</p>
                <p className="text-3xl font-bold">R{wallet?.balance_zar?.toFixed(0) || '0'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white/10 rounded-xl p-3">
            <p className="text-xs text-white/80 mb-1">Ready for betting</p>
            <p className="text-xs text-white/60 italic">{randomWalletMessage}</p>
            <p className="text-sm font-semibold inline-flex items-center gap-1.5">
              {wallet?.balance_zar && wallet.balance_zar >= 50 ? (
                <>
                  <Check className="h-4 w-4" /> Sufficient for minimum bets
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4" /> Add funds to start betting
                </>
              )}
            </p>
          </div>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            onClick={() => setShowPaymentSheet(true)}
            fullWidth
            leftIcon={<Plus className="h-5 w-5" />}
          >
            Deposit
          </Button>

          <Button
            variant="secondary"
            onClick={() => setShowWithdrawalForm(true)}
            fullWidth
            leftIcon={<ArrowUpRight className="h-5 w-5" />}
          >
            Withdraw
          </Button>
        </div>

        {/* Quick Deposit */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Quick Deposit</h3>
          <div className="grid grid-cols-2 gap-3 mb-4">
            {quickAmounts.map(amount => (
              <button
                key={amount}
                onClick={() => {
                  setDepositAmount(amount);
                  setShowPaymentSheet(true);
                }}
                className="px-4 py-3 rounded-xl font-bold border border-gray-200 hover:border-emerald-300 hover:bg-emerald-50 transition"
              >
                R{amount}
              </button>
            ))}
          </div>
          {/* Custom amount */}
          <div className="flex items-center space-x-2 mb-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">R</span>
              <input
                type="number"
                min={50}
                max={100000}
                inputMode="numeric"
                value={customDeposit}
                onChange={(e) => { setCustomDeposit(e.target.value); setDepositError(''); }}
                placeholder="Other amount (min R50)"
                className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
              />
            </div>
            <Button
              onClick={() => {
                const v = parseInt(customDeposit, 10);
                if (isNaN(v) || v < 50) { setDepositError('Minimum deposit is R50'); return; }
                if (v > 100000) { setDepositError('Maximum deposit is R100,000'); return; }
                setDepositAmount(v);
                setShowPaymentSheet(true);
              }}
            >
              Add
            </Button>
          </div>
          {depositError && (
            <p className="text-xs text-red-600 text-center mb-2">{depositError}</p>
          )}
          <p className="text-xs text-gray-500 text-center">
            Instant deposits • Secure payment processing
          </p>
          <p className="text-[11px] text-gray-400 text-center mt-1">
            You get every rand you add. A <span className="font-semibold text-gray-500">1%</span> FairPlay fee + card processing is added on top so we can keep the app running.
          </p>
        </Card>

        {/* Withdrawal Requests */}
        {withdrawals.length > 0 && (
          <Card>
            <h3 className="font-semibold text-gray-800 mb-4">Withdrawals</h3>
            <div className="space-y-3">
              {withdrawals.map((w) => {
                const chip = withdrawalStatusChip(w.status);
                const cancellable = w.status === 'cooling_off' || w.status === 'ready';
                return (
                  <div key={w.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className="font-semibold text-gray-800">R{w.amount_zar.toFixed(2)}</p>
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded ${chip.className}`}>
                          {chip.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Requested {new Date(w.requested_at).toLocaleDateString()}
                        {w.status === 'cooling_off' && w.days_until_ready != null && (
                          <> • ready in {w.days_until_ready} day{w.days_until_ready === 1 ? '' : 's'}</>
                        )}
                      </p>
                    </div>
                    {cancellable && (
                      <button
                        onClick={() => handleCancelWithdrawal(w.id)}
                        disabled={cancellingId === w.id}
                        className="text-sm font-medium text-red-600 hover:text-red-700 disabled:opacity-50 px-3 py-1.5 rounded-lg hover:bg-red-50 transition"
                      >
                        {cancellingId === w.id ? 'Cancelling…' : 'Cancel'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              Cancelling a pending withdrawal returns the funds to your wallet instantly.
            </p>
          </Card>
        )}

        {/* Recent Activity */}
        <Card>
          <h3 className="font-semibold text-gray-800 mb-4">Recent Activity</h3>
          {recentTransactions.length === 0 ? (
            <EmptyState
              image="/images/empty-wallet.png"
              title="No transactions yet"
              subtitle="Your deposits, bets and payouts will show up here."
            />
          ) : (
            <div className="space-y-3">
              {recentTransactions.map((tx) => {
                const expanded = expandedTransaction === tx.id;
                const details = (tx.description || '').split(' · ').filter(Boolean);
                return (
                <button
                  type="button"
                  key={tx.id}
                  onClick={() => setExpandedTransaction(expanded ? null : tx.id)}
                  aria-expanded={expanded}
                  className="w-full text-left p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-3 min-w-0">
                    <div className="bg-white p-2 rounded-lg">
                      {getTransactionIcon(tx.transaction_type)}
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-800 capitalize">
                        {tx.transaction_type.replace('_', ' ')}
                        {tx.status === 'pending' && (
                          <span className="ml-2 text-[10px] font-semibold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Pending</span>
                        )}
                        {tx.status === 'failed' && (
                          <span className="ml-2 text-[10px] font-semibold uppercase bg-red-100 text-red-700 px-1.5 py-0.5 rounded">Failed</span>
                        )}
                      </p>
                      {details.length > 1 && <p className="text-xs text-gray-600 truncate">{details.slice(1, 3).join(' · ')}</p>}
                      <p className="text-xs text-gray-400">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <div className={`font-bold ${getTransactionColor(tx.transaction_type)}`}>
                        {isCredit(tx.transaction_type) ? '+' : '-'}R{tx.amount_zar.toFixed(2)}
                      </div>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                  {expanded && (
                    <div className="mt-3 ml-11 border-t border-gray-200 pt-3 space-y-1">
                      {details.map((detail, index) => (
                        <p key={`${tx.id}-${index}`} className="text-xs text-gray-600">
                          {index === 0 ? 'What' : index === 1 ? 'Bet' : index === 2 ? 'Round' : 'Detail'}: <span className="font-medium text-gray-800">{detail}</span>
                        </p>
                      ))}
                      <p className="text-xs text-gray-600">Status: <span className="font-medium text-gray-800 capitalize">{tx.status}</span></p>
                      <p className="text-xs text-gray-600">Method: <span className="font-medium text-gray-800 capitalize">{tx.payment_method.replace('_', ' ')}</span></p>
                      <p className="text-xs text-gray-600">When: <span className="font-medium text-gray-800">{new Date(tx.created_at).toLocaleString()}</span></p>
                      {tx.reference && <p className="text-xs text-gray-600">Reference: <span className="font-medium text-gray-800">{tx.reference}</span></p>}
                    </div>
                  )}
                </button>
              );})}
            </div>
          )}
        </Card>

        {/* Payment Sheet */}
        {showPaymentSheet && (
          <PaymentSheet
            isOpen={showPaymentSheet}
            amount={depositAmount}
            onClose={() => setShowPaymentSheet(false)}
          />
        )}

        {/* Withdrawal Form */}
        {showWithdrawalForm && (
          <div className="fixed inset-0 z-[60] flex items-end justify-center">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setShowWithdrawalForm(false)}
            />
            
            {/* Sheet */}
            <div className="relative w-full max-w-[375px] max-h-[90dvh] overflow-y-auto bg-white rounded-t-3xl animate-slide-up pb-[env(safe-area-inset-bottom)]">
              {/* Handle */}
              <div className="flex justify-center pt-3 pb-2">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>

              {/* Header */}
              <div className="px-6 pb-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-gray-800">Withdraw Funds</h2>
                  <button 
                    onClick={() => setShowWithdrawalForm(false)}
                    className="p-2 hover:bg-gray-100 rounded-full transition"
                  >
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Available balance: R{wallet ? (wallet.balance / 100).toFixed(2) : '0.00'}
                </p>
              </div>

              {/* Form */}
              <div className="px-6 py-4 space-y-4">
                {/* Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Amount (R)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500">R</span>
                    </div>
                    <input
                      type="text"
                      value={withdrawalAmount}
                      onChange={(e) => setWithdrawalAmount(e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-8 pr-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex justify-between mt-1">
                    <p className="text-xs text-gray-500">Min: R100 | Max: R10,000</p>
                    <p className="text-xs text-gray-500">3-day cooling off period</p>
                  </div>
                </div>

                {/* Payout account (from profile) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Paying to
                  </label>
                  {bank?.has_details ? (
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex items-start gap-3">
                      <Landmark className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-800 truncate">{bank.bank}</p>
                        <p className="text-sm text-gray-600 truncate">
                          ••••{bank.account_number.slice(-4)} · {bank.account_holder}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => { setShowWithdrawalForm(false); navigate('/profile'); }}
                        className="text-xs font-semibold text-emerald-700 hover:underline flex-shrink-0"
                      >
                        Change
                      </button>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                      <p className="text-sm text-amber-800 font-medium">No payout account yet</p>
                      <p className="text-xs text-amber-700 mt-0.5 mb-2">
                        Add your bank account once and we'll use it for every withdrawal.
                      </p>
                      <button
                        type="button"
                        onClick={() => { setShowWithdrawalForm(false); navigate('/profile'); }}
                        className="w-full bg-amber-600 text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-700 transition"
                      >
                        Set up in Profile
                      </button>
                    </div>
                  )}
                </div>

                {/* Security Notice */}
                <div className="bg-amber-50 rounded-xl p-3 flex items-start space-x-2">
                  <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-amber-700 font-medium">Security Notice</p>
                    <p className="text-xs text-amber-600">
                      Withdrawals are subject to a 3-day cooling off period for your protection.
                    </p>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  onClick={handleWithdrawal}
                  disabled={isProcessingWithdrawal || !withdrawalAmount || !bank?.has_details}
                  className="w-full bg-emerald-600 text-white py-4 rounded-xl font-medium flex items-center justify-center space-x-2 hover:bg-emerald-700 transition active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isProcessingWithdrawal ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <ArrowUpRight className="h-5 w-5" />
                      <span>Request Withdrawal</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SimpleWallet;
